import { useCallback, useMemo, useState } from "react";
import {
  TbBrandWhatsapp,
  TbPlayerPlayFilled,
  // TbPlugConnected,
} from "react-icons/tb";
import styles from "../_shared/productLanding.module.css";
import SeoHead from "../../components/Seo/SeoHead";
import WhatsAppMockup from "../../components/Marketing/WhatsAppMockup";
import FaqAccordion from "../../components/Marketing/FaqAccordion";
import TestimonialBlock from "../../components/Marketing/TestimonialBlock";
import VideoSection from "../../components/VideoSection/VideoSection";
import VideoModal from "../../components/VideoModal/VideoModal";
import TrialProductModal from "../../components/TrialProductModal/TrialProductModal";
import { CTA_PLACEMENTS } from "../../analytics";

// HeroBanner e HealthBeautySection continuam existindo mas não são usados aqui
// para evitar o H1 duplicado e a estrutura genérica

const WHATSAPP_PHONE = "5551992747402";

/**
 * ⚠️ Esta página NÃO declara mais um `CTA_CTX` nem dispara `trial_cta_click`.
 * Os quatro CTAs de teste abrem o {@link TrialProductModal}, e o evento sai de
 * lá — quando o produto já é conhecido —, com o contexto resolvido da rota por
 * `resolveContextFromPath` (`/` → home/general). Disparar aqui TAMBÉM dobraria
 * o topo do funil e afundaria a taxa de conversão por artefato de medição.
 */

const YOUTUBE_ID = "lkcbXYnJAR8"; // vídeo de apresentação — https://youtu.be/lkcbXYnJAR8

const HOW_IT_WORKS = [
  {
    step: "1",
    title: "Escolha a solução que melhor te atende",
    text: "Precisa que alguém responda as perguntas básicas da empresa, ou precisa gerenciar seus agendamentos?",
  },
  {
    step: "2",
    title: "Conecte seu WhatsApp",
    text: "Em menos de 5 minutos, seu número já responde automaticamente.",
  },
  {
    step: "3",
    title: "Escolha o seu agente",
    text: "Indique como o seu chatbot deve responder os seus clientes",
  },
];

const FEATURES = [
  {
    title: "Mantenha seus leads aquecidos",
    text: "Seus clientes não ficam sem resposta",
  },
  {
    title: "Não perca nenhum agendamento.",
    text: "Sem depender de você estar online. O bot atende 24/7.",
  },
  {
    title: "Ele lembra do seu cliente",
    text: "Não parece frio, tem memória do que seu cliente costuma perguntar ou agendar.",
  },
];
const DEMO_MESSAGES = [
  { type: "out", text: "Oi, vocês abre domingo? " },
  {
    type: "in",
    text: "Olá! Infelizmente no domingo não abrimos, mas de Segunda a Sexta das 09:00 as 18:00 estamos prontos para te antender.",
  },
  { type: "out", text: "Muito obrigado!" },
];

const FAQ_ITEMS = [
  {
    question: "Qual a diferença entre o Chatbot de Atendimento e o de Agendamento?",
    answer:
      "O de Atendimento responde: tira dúvidas, informa preços e guarda o contato de quem chamou. O de Agendamento faz tudo isso E cuida da agenda — marca, remarca, cancela e lembra o cliente antes do horário. Se você não trabalha com hora marcada, o de Atendimento resolve. Se trabalha, o de Agendamento é o seu.",
  },
  {
    question: "Vou perder meu WhatsApp Business?",
    answer:
      "Não. Somos homologados pela Meta com a API Coexistence: seu número continua funcionando normalmente no app e no WhatsApp Web, com o histórico e os contatos onde sempre estiveram. O bot passa a responder junto, não no lugar.",
  },
  {
    question: "Funciona no Instagram também?",
    answer:
      "Sim. O bot atende no WhatsApp e no Direct do Instagram, com as mesmas respostas configuradas — você cadastra uma vez e vale para os dois canais.",
  },
  {
    question: "Preciso ter conhecimento técnico pra usar?",
    answer:
      "Não. Você conecta o WhatsApp pelo painel e escreve as respostas em português, como escreveria para um funcionário novo. Não há fluxo pra desenhar nem código pra escrever.",
  },
  {
    question: "Quanto tempo leva pra configurar?",
    answer:
      "Em média 15 minutos: conectar o número, escolher a solução e cadastrar as primeiras informações do negócio. Dá pra ir ajustando depois, com o bot já no ar.",
  },
  {
    question: "Posso cancelar quando quiser?",
    answer:
      "Sim. Sem fidelidade, sem multa e sem taxa de adesão — você cancela pelo painel. O teste de 14 dias também não pede cartão.",
  },
];

const ORG_LD = {
  "@context": "https://schema.org",
  "@type": "Organization",
  name: "Nexus Chatbot",
  url: "https://nexustech.tec.br",
  logo: "https://nexustech.tec.br/logo_nexus_sem_fundo.png",
  contactPoint: {
    "@type": "ContactPoint",
    telephone: "+55-51-99274-7402",
    contactType: "customer service",
    email: "contato@nexustech.tec.br",
  },
};

const FAQ_LD = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: FAQ_ITEMS.map((f) => ({
    "@type": "Question",
    name: f.question,
    acceptedAnswer: { "@type": "Answer", text: f.answer },
  })),
};

const Home = () => {
  const [videoOpen, setVideoOpen] = useState(false);
  const openVideo = useCallback(() => setVideoOpen(true), []);
  const closeVideo = useCallback(() => setVideoOpen(false), []);

  // O placement do CTA que abriu o modal viaja no estado porque quem dispara o
  // `trial_cta_click` é o modal, não o botão. Sem isso, os quatro CTAs desta
  // página ficariam indistinguíveis no GA4.
  const [trialPlacement, setTrialPlacement] = useState(null);
  const openTrialModal = useCallback(
    (placement) => setTrialPlacement(placement),
    [],
  );
  const closeTrialModal = useCallback(() => setTrialPlacement(null), []);

  const waHref = useMemo(() => {
    const page =
      typeof window !== "undefined"
        ? window.location.href
        : "https://nexustech.tec.br";
    const text = `Olá! Gostaria de falar com a equipe da Nexus Tech via site. \n\nPágina:${page}`;
    return `https://wa.me/${WHATSAPP_PHONE}?text=${encodeURIComponent(text)}`;
  }, []);

  return (
    <>
      <SeoHead
        title="Chatbot de WhatsApp e Instagram 24h | Nexus Chatbot"
        description="Atenda e agende pelo WhatsApp e Instagram 24 horas. Homologado pela Meta com API Coexistence — você não perde seu WhatsApp Business. Teste grátis 14 dias, sem cartão."
        canonical="https://nexustech.tec.br/"
        ogImage="https://nexustech.tec.br/og-home.jpg"
        jsonLd={[ORG_LD, FAQ_LD]}
      />

      <main className={styles.main} role="main">
        {/* ── SEÇÃO 1: HERO ── */}
        <section className={styles.heroSection}>
          <div className={`${styles.contentWide} ${styles.heroLayout}`}>
            <div className={styles.heroText}>
              <h1 className={styles.heroH1}>
                Atenda seus clientes pelo WhatsApp e Instagram 24 horas
              </h1>
              <p className={styles.heroH1Subtext}>
                Homologado pela Meta com API Coexistence
              </p>
              <p className={styles.heroSubtitle}>
                Você não perde seu WhatsApp Business, ele continua no APP ou
                Web.
              </p>

              <div className={styles.heroCtas}>
                <button
                  type="button"
                  className={styles.ctaPrimary}
                  onClick={() => openTrialModal(CTA_PLACEMENTS.HERO)}
                >
                  Começar meu teste grátis
                </button>
                <button
                  type="button"
                  className={styles.ctaWatch}
                  onClick={openVideo}
                >
                  <TbPlayerPlayFilled
                    className={styles.ctaWatchIcon}
                    aria-hidden="true"
                  />
                  Ver apresentação
                </button>
              </div>

              <p className={styles.heroCopy}>
                Sem cartão. Cancele quando quiser. Configuração em 15 minutos.
              </p>
            </div>

            <div className={styles.heroMockup}>
              <WhatsAppMockup messages={DEMO_MESSAGES} looping />
            </div>
          </div>
        </section>

        {/* ── SEÇÃO 1.5: VÍDEO DE APRESENTAÇÃO ── */}
        <VideoSection onPlay={openVideo} />

        {/* ── SEÇÃO 2: PARA QUEM É ── */}
        {/* <section id="segmentos" className={styles.tintedSection}>
          <div className={styles.contentWide}>
            <h2 className={styles.sectionHeading}>
              Feito para o seu segmento, não genérico
            </h2>
            <p className={styles.sectionSubtitle}>
              Soluções pensadas para quem atende com hora marcada.
            </p>
            <div className={styles.segmentGrid}></div>


             <div className={styles.nextfitNote}>
              <span className={styles.nextfitIcon} aria-hidden="true">
                <TbPlugConnected />
              </span>
              <div>
                <h3 className={styles.nextfitTitle}>
                  Já usa NextFit? O bot conversa com ele.
                </h3>
                <p className={styles.nextfitText}>
                  O bot consulta (somente leitura) agenda, aulas, planos e
                  contratos do seu NextFit.
                </p>
              </div>
            </div>
          </div>
        </section> */}

        {/* ── SEÇÃO 3: COMO FUNCIONA ── */}
        <section className={styles.plainSection}>
          <div className={styles.contentNarrow}>
            <h2 className={styles.sectionHeading}>
              Configuração em 15 minutos. Funciona em segundos.
            </h2>

            <div className={styles.stepsGrid}>
              {HOW_IT_WORKS.map((item) => (
                <div key={item.step} className={styles.step}>
                  <div className={styles.stepCircle}>{item.step}</div>
                  <h3 className={styles.stepTitle}>{item.title}</h3>
                  <p className={styles.stepText}>{item.text}</p>
                </div>
              ))}
            </div>

            <div className={styles.ctaBlock}>
              <button
                type="button"
                className={styles.ctaPrimary}
                onClick={() => openTrialModal(CTA_PLACEMENTS.MIDDLE)}
              >
                Quero usar no meu negócio
              </button>
            </div>
          </div>
        </section>

        {/* ── SEÇÃO 4: RECURSOS ── */}
        <section className={styles.tintedSection}>
          <div className={styles.contentNarrow}>
            <h2 className={styles.sectionHeading}>
              Tudo o que sua agenda precisa, num só chat
            </h2>

            <div className={styles.featuresGrid}>
              {FEATURES.map((feat) => (
                <div key={feat.title} className={styles.featureCard}>
                  <h3 className={styles.featureTitle}>{feat.title}</h3>
                  <p className={styles.featureText}>{feat.text}</p>
                </div>
              ))}
            </div>

            <div className={styles.ctaBlock}>
              <button
                type="button"
                className={styles.ctaPrimary}
                onClick={() => openTrialModal(CTA_PLACEMENTS.FEATURES)}
              >
                Ativar meu chatbot agora
              </button>
            </div>
          </div>
        </section>

        {/* ── SEÇÃO 5: PROVA SOCIAL ── */}
        {/* TestimonialBlock retorna null se array vazio — não renderiza placeholder */}
        <TestimonialBlock testimonials={[]} />

        {/* ── SEÇÃO 7: FAQ ── */}
        <section className={styles.tintedSection}>
          <div className={styles.contentNarrowest}>
            <h2 className={styles.sectionHeading}>
              Perguntas que todo mundo faz
            </h2>
            <FaqAccordion items={FAQ_ITEMS} />
            {/* Bloco E: botão "Falar no WhatsApp" removido — FAB já é onipresente */}
          </div>
        </section>

        {/* ── CTA FINAL ── */}
        <section className={styles.ctaFinalSection}>
          <div className={styles.contentCenter}>
            <h2 className={styles.ctaFinalHeading}>
              Pronto para deixar de atender telefone manualmente?
            </h2>
            <p className={styles.ctaFinalText}>
              14 dias grátis. Sem cartão. Cancelamento a qualquer momento.
            </p>
            <div className={styles.ctaFinalCtas}>
              <button
                type="button"
                className={styles.ctaFinalBtn}
                onClick={() => openTrialModal(CTA_PLACEMENTS.FINAL)}
              >
                Começar grátis por 14 dias
              </button>
              <a
                href={waHref}
                className={styles.ctaFinalWhats}
                target="_blank"
                rel="noopener noreferrer"
              >
                <TbBrandWhatsapp aria-hidden="true" />
                Falar no WhatsApp
              </a>
            </div>
          </div>
        </section>

        {/* ── WHATSAPP FAB (fixo) ── */}
        <a
          href={waHref}
          className={styles.whatsappFab}
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Abrir conversa no WhatsApp"
        >
          <TbBrandWhatsapp className={styles.whatsappIcon} aria-hidden="true" />
          <span className={styles.srOnly}>Conversar no WhatsApp</span>
        </a>
      </main>

      {/* Modal do vídeo — fora do <main> por ser conteúdo sobreposto */}
      <VideoModal
        open={videoOpen}
        youtubeId={YOUTUBE_ID}
        onClose={closeVideo}
      />

      {/* Escolha do produto — todos os CTAs de teste desta página passam por aqui */}
      <TrialProductModal
        isOpen={trialPlacement !== null}
        onClose={closeTrialModal}
        ctaPlacement={trialPlacement}
      />
    </>
  );
};

export default Home;
